export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

export const CURRENCY_STORAGE_KEY = 'admin_display_currency';
export const CONVERSION_VIEW_CURRENCY_KEY = 'admin_conversion_view_currency';
export const DEFAULT_CURRENCY = 'EUR';
export const DEFAULT_CONVERSION_CURRENCY = 'INR';

/** Currencies available in pickers and display settings */
export const ALL_CURRENCIES: CurrencyOption[] = [
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QR' },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KD' },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BD' },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
];

/** @deprecated use ALL_CURRENCIES — kept for settings page compatibility */
export const CURRENCY_OPTIONS = ALL_CURRENCIES.filter((c) =>
  ['USD', 'EUR', 'GBP', 'SGD', 'INR', 'AED'].includes(c.code),
);

export type CurrencyCode = string;

export function getCurrencyOption(code: string): CurrencyOption | undefined {
  return ALL_CURRENCIES.find((c) => c.code === code);
}

export function isSupportedCurrency(code: string): boolean {
  return ALL_CURRENCIES.some((c) => c.code === code);
}

export function loadStoredCurrency(): string {
  const stored = localStorage.getItem(CURRENCY_STORAGE_KEY);
  if (stored && isSupportedCurrency(stored)) {
    return stored;
  }
  return DEFAULT_CURRENCY;
}

export function loadConversionViewCurrency(): string {
  const stored = localStorage.getItem(CONVERSION_VIEW_CURRENCY_KEY);
  if (stored && isSupportedCurrency(stored)) {
    return stored;
  }
  return DEFAULT_CONVERSION_CURRENCY;
}

export function getCurrencySymbol(code: string): string {
  return getCurrencyOption(code)?.symbol ?? code;
}

/** Symbol shown in the offer form. AED stays "Dh" so the label stays readable. */
export function displayCurrencyMark(code: string): string {
  if (code === 'AED') return 'Dh';
  return getCurrencySymbol(code);
}

/**
 * Mark used inside offer PDFs. Euro, dollar, and pound draw correctly in the
 * standard PDF font. The rupee sign does not, so the PDF uses Rs.
 */
export function pdfCurrencyMark(code: string): string {
  if (code === 'INR') return 'Rs';
  const symbol = displayCurrencyMark(code);
  if (
    symbol === '€' ||
    symbol === '$' ||
    symbol === '£' ||
    symbol === '¥' ||
    /^[\u0020-\u007E]+$/.test(symbol)
  ) {
    return symbol;
  }
  return code || symbol;
}

export function formatPdfCurrencyAmount(amount: number, code: string): string {
  const formatted = Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${pdfCurrencyMark(code || 'USD')} ${formatted}`;
}

export function formatCurrencyAmount(amount: number, code: string): string {
  const symbol = getCurrencySymbol(code);
  const formatted = Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return `${symbol}${formatted}`;
}

export function formatCurrencyLabel(base: string, code: string): string {
  return `${base} (${code})`;
}
